import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ImageField } from "@/components/ImageField";
import { SortableList } from "@/components/SortableList";
import { ICONS } from "@/components/site/SiteRenderer";
import { FONTS, PAGES, block, txt, uid, same } from "@/lib/siteDefaults";
import { ChevronUp, ChevronDown, Copy, Eye, EyeOff, Trash2, Languages, Loader2, Plus, Type, Heading, MousePointerClick, Image, Minus, MoveVertical, MapPin, FileText, Upload } from "lucide-react";
import { Row, Num, Color, Text, Pick, Flag, Group, PosXY } from "./ui";

const FONT_OPTS = [["", "По подразбиране"], ...FONTS.map((f) => [f, f])];
const LINK_OPTS = [...PAGES.map(([, l, href]) => [href, `Страница: ${l}`]), ["custom", "Друг адрес (URL)…"]];
export const BLOCK_TYPES = [["heading", "Заглавие", Heading], ["text", "Текст", Type], ["button", "Бутон", MousePointerClick], ["image", "Снимка", Image], ["divider", "Линия", Minus], ["spacer", "Празно място", MoveVertical], ["icon", "Икона + текст", MapPin], ["file", "PDF / A4", FileText]];
export const BLOCK_LABEL = Object.fromEntries(BLOCK_TYPES.map(([k, l]) => [k, l]));

export const Actions = ({ onUp, onDown, onDup, hidden, onHide, onDelete, testid }) => (
  <div className="flex gap-1">
    <Button size="icon" variant="outline" className="h-8 w-8" onClick={onUp} title="Нагоре" data-testid={`${testid}-up`}><ChevronUp className="w-4 h-4" /></Button>
    <Button size="icon" variant="outline" className="h-8 w-8" onClick={onDown} title="Надолу" data-testid={`${testid}-down`}><ChevronDown className="w-4 h-4" /></Button>
    <Button size="icon" variant="outline" className="h-8 w-8" onClick={onDup} title="Копирай" data-testid={`${testid}-duplicate`}><Copy className="w-4 h-4" /></Button>
    <Button size="icon" variant="outline" className="h-8 w-8" onClick={onHide} title={hidden ? "Покажи" : "Скрий"} data-testid={`${testid}-hide`}>{hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</Button>
    <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={onDelete} title="Изтрий" data-testid={`${testid}-delete`}><Trash2 className="w-4 h-4" /></Button>
  </div>
);

function TranslateBtn({ value, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!value?.bg) return toast.error("Първо въведете текста на български");
    setBusy(true);
    try { const { data } = await api.post("/ai/translate-text", { text: value.bg }); onDone({ ...value, ...data }); toast.success("Преведено на EN/DE/FR"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return <Button size="sm" variant="outline" onClick={go} disabled={busy} className="gap-1 h-7 text-xs" data-testid="block-ai-translate">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />} AI превод от БГ</Button>;
}

function DocumentField({ b, set }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("Файлът е твърде голям (макс. 20 MB)");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads/document", fd);
      set({ src: data.url, pages: data.pages, kind: data.kind, name: data.name });
      toast.success("Файлът е качен – натиснете „Публикувай“, за да се покаже на сайта");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <Row label="Файл (PDF или снимка)">
      <input ref={ref} type="file" accept="application/pdf,.pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="document-input" />
      {b.name && <div className="text-xs text-forest-deep truncate" data-testid="document-name">{b.name} · {b.pages?.length || 0} стр.</div>}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} className="gap-1" data-testid="document-upload-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{b.src ? "Смени файла" : "Качи файл"}
        </Button>
        {b.src && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => set({ src: "", pages: [], kind: "", name: "" })} data-testid="document-remove-btn"><Trash2 className="w-4 h-4" /></Button>}
      </div>
    </Row>
  );
}

export function BlockPanel({ b, cols, lang, set, actions }) {
  const s = b.style || {};
  const st = (patch) => set({ style: { ...s, ...patch } });
  const hasText = ["heading", "text", "button", "icon"].includes(b.type);
  const isCustom = b.type === "button" && !PAGES.some(([, , h]) => h === b.href);
  return (
    <div className="space-y-3" data-testid="block-panel">
      <div className="flex items-center justify-between"><div className="font-semibold text-forest-deep">{BLOCK_LABEL[b.type]}</div>{actions}</div>
      {hasText && (
        <Row label={`Текст (${lang.toUpperCase()})`}>
          <Textarea value={b.text?.[lang] || ""} placeholder={b.text?.bg}
            onChange={(e) => set({ text: { ...b.text, [lang]: e.target.value } })} rows={3} data-testid="block-text" />
          <TranslateBtn value={b.text} onDone={(text) => set({ text })} />
        </Row>
      )}
      {b.type === "heading" && <Pick label="Ниво" value={s.level} onChange={(v) => st({ level: v })} options={[["h1", "H1 – главно"], ["h2", "H2"], ["h3", "H3"], ["h4", "H4"]]} testid="block-level" />}
      {b.type === "button" && (<>
        <Pick label="Връзка" value={isCustom ? "custom" : b.href} onChange={(v) => set({ href: v === "custom" ? "https://" : v })} options={LINK_OPTS} testid="block-link" />
        {isCustom && <Text label="Адрес" value={b.href} onChange={(v) => set({ href: v })} testid="block-href" />}
        <div className="grid grid-cols-2 gap-2"><Color label="Фон" value={s.bg_color} onChange={(v) => st({ bg_color: v })} testid="block-btn-bg" /><Color label="Текст" value={s.text_color} onChange={(v) => st({ text_color: v })} testid="block-btn-color" /></div>
        <Num label="Заобляне" value={s.radius} max={40} onChange={(v) => st({ radius: v })} testid="block-radius" />
        <Flag label="На един ред със съседни бутони" value={s.inline} onChange={(v) => st({ inline: v })} testid="block-inline" />
      </>)}
      {b.type === "file" && (<>
        <DocumentField b={b} set={set} />
        <Num label="Макс. ширина" value={s.max_width ?? 900} min={300} max={1400} unit="px" onChange={(v) => st({ max_width: v })} testid="document-width" />
        <Flag label="Бутон за изтегляне" value={s.show_download !== false} onChange={(v) => st({ show_download: v })} testid="document-download-toggle" />
        <Row label={`Текст на бутона (${lang.toUpperCase()})`}>
          <input value={b.text?.[lang] || ""} placeholder={b.text?.bg} onChange={(e) => set({ text: { ...b.text, [lang]: e.target.value } })} className="w-full h-9 border rounded-md px-2 text-sm" data-testid="document-label" />
        </Row>
      </>)}
      {b.type === "icon" && <Pick label="Икона" value={b.icon} onChange={(v) => set({ icon: v })} options={Object.keys(ICONS).map((k) => [k, k])} testid="block-icon" />}
      {b.type === "image" && (<>
        <ImageField label="Снимка" value={b.src} onChange={(v) => set({ src: v })} testid="block-image" />
        <Text label="Описание (alt)" value={b.alt} onChange={(v) => set({ alt: v })} />
        <Num label="Ширина" value={s.width ?? 100} min={10} max={100} unit="%" onChange={(v) => st({ width: v })} testid="block-width" />
        <Num label="Височина" value={s.height} min={60} max={900} unit="px" onChange={(v) => st({ height: v })} testid="block-height" />
        <Num label="Заобляне" value={s.radius} max={80} onChange={(v) => st({ radius: v })} testid="block-radius" />
        <PosXY label="Фокус" value={s.object_position} onChange={(v) => st({ object_position: v })} testid="block-focus" />
        <Num label="Яркост" value={s.brightness ?? 100} min={30} max={150} unit="%" onChange={(v) => st({ brightness: v })} testid="block-brightness" />
      </>)}
      {["divider", "spacer"].includes(b.type) && (<>
        {b.type === "divider" && <><Num label="Дължина" value={s.width} max={600} unit="px" onChange={(v) => st({ width: v })} /><Color label="Цвят" value={s.color} onChange={(v) => st({ color: v })} /></>}
        {b.type === "spacer" && <Num label="Височина" value={s.height} max={300} unit="px" onChange={(v) => st({ height: v })} testid="block-spacer" />}
      </>)}
      {hasText && (
        <Group title="Шрифт">
          <Pick label="Шрифт" value={s.font_family} onChange={(v) => st({ font_family: v })} options={FONT_OPTS} testid="block-font" />
          <Num label="Размер" value={s.font_size} min={10} max={110} unit="px" onChange={(v) => st({ font_size: v })} testid="block-font-size" />
          {b.type !== "button" && <Color label="Цвят" value={s.color} onChange={(v) => st({ color: v })} testid="block-color" />}
          <div className="grid grid-cols-2 gap-2"><Flag label="Удебелен" value={s.bold} onChange={(v) => st({ bold: v })} testid="block-bold" /><Flag label="Курсив" value={s.italic} onChange={(v) => st({ italic: v })} testid="block-italic" /></div>
          <Num label="Междуредие" value={s.line_height ?? 1.5} min={0.9} max={2.5} step={0.05} onChange={(v) => st({ line_height: v })} />
          {b.type !== "button" && <Num label="Ширина на текста" value={s.width ?? 100} min={20} max={100} unit="%" onChange={(v) => st({ width: v })} testid="block-text-width" />}
        </Group>
      )}
      <Group title="Позиция и отстояния">
        <Pick label="Подравняване" value={s.align || "left"} onChange={(v) => st({ align: v })} options={[["left", "Ляво"], ["center", "Център"], ["right", "Дясно"]]} testid="block-align" />
        {cols > 1 && <Pick label="Колона" value={String(b.col || 0)} onChange={(v) => set({ col: Number(v) })} options={Array.from({ length: cols }, (_, i) => [String(i), `Колона ${i + 1}`])} testid="block-col" />}
        <div className="grid grid-cols-2 gap-2"><Num label="Отгоре" value={s.mt} max={200} onChange={(v) => st({ mt: v })} /><Num label="Отдолу" value={s.mb} max={200} onChange={(v) => st({ mb: v })} testid="block-mb" /></div>
        <div className="grid grid-cols-2 gap-2"><Num label="Изместване X" value={s.offset_x} min={-400} max={400} onChange={(v) => st({ offset_x: v })} testid="block-offset-x" /><Num label="Изместване Y" value={s.offset_y} min={-400} max={400} onChange={(v) => st({ offset_y: v })} testid="block-offset-y" /></div>
        <Num label="Прозрачност" value={Math.round((s.opacity ?? 1) * 100)} min={10} max={100} unit="%" onChange={(v) => st({ opacity: v / 100 })} />
      </Group>
    </div>
  );
}

export function SectionPanel({ sec, set, actions, onAddBlock, onSelectBlock, onReorderBlocks }) {
  const s = sec.style;
  const st = (patch) => set({ style: { ...s, ...patch } });
  return (
    <div className="space-y-3" data-testid="section-panel">
      <div className="flex items-center justify-between"><div className="font-semibold text-forest-deep">Секция{sec.type === "dynamic" ? " (автоматична)" : ""}</div>{actions}</div>
      {sec.type !== "dynamic" && (<>
        <Row label="Добави елемент">
          <div className="grid grid-cols-4 gap-1">
            {BLOCK_TYPES.map(([k, l, Ico]) => (
              <button key={k} onClick={() => onAddBlock(k)} className="flex flex-col items-center gap-0.5 p-1.5 rounded-lg border border-gold/20 hover:bg-gold/10 text-[10px]" data-testid={`add-block-${k}`}><Ico className="w-4 h-4" />{l}</button>
            ))}
          </div>
        </Row>
        <Row label="Елементи (влачете за подредба)">
          <SortableList ids={sec.blocks.map((b) => b.id)} onReorder={onReorderBlocks}>
            {(id, handle) => {
              const b = sec.blocks.find((x) => x.id === id);
              return (
                <div className="flex items-center gap-1 text-xs border border-gold/15 rounded-lg px-1 py-1 bg-white">
                  {handle}
                  <button className="flex-1 text-left truncate" onClick={() => onSelectBlock(id)}>{BLOCK_LABEL[b.type]}{b.text?.bg ? `: ${b.text.bg.slice(0, 26)}` : ""}</button>
                  {s.columns > 1 && <span className="opacity-50">К{(b.col || 0) + 1}</span>}
                  {b.hidden && <EyeOff className="w-3 h-3 opacity-50" />}
                </div>
              );
            }}
          </SortableList>
        </Row>
        <Group title="Оформление">
          <Pick label="Колони" value={String(s.columns || 1)} onChange={(v) => st({ columns: Number(v) })} options={[["1", "1"], ["2", "2"], ["3", "3"]]} testid="section-columns" />
          <Num label="Разстояние между колоните" value={s.gap} max={120} onChange={(v) => st({ gap: v })} />
          <Num label="Вътрешно отстояние" value={s.padding_y} max={240} onChange={(v) => st({ padding_y: v })} testid="section-padding" />
          <Num label="Минимална височина" value={s.min_height} max={100} unit="% екран" onChange={(v) => st({ min_height: v })} testid="section-min-height" />
          <Pick label="Вертикално" value={s.valign || "start"} onChange={(v) => st({ valign: v })} options={[["start", "Горе"], ["center", "Център"], ["end", "Долу"]]} />
          <Num label="Макс. ширина" value={s.max_width} min={600} max={1920} unit="px" onChange={(v) => st({ max_width: v })} />
        </Group>
      </>)}
      <Group title="Фон">
        <Color label="Цвят на фона" value={s.bg_color} onChange={(v) => st({ bg_color: v })} testid="section-bg-color" />
        <ImageField label="Фонова снимка" value={s.bg_image} onChange={(v) => st({ bg_image: v })} testid="section-bg-image" />
        {s.bg_image && <PosXY label="Позиция" value={s.bg_position} onChange={(v) => st({ bg_position: v })} testid="section-bg-pos" />}
        <Num label="Яркост на снимката" value={s.brightness ?? 100} min={20} max={150} unit="%" onChange={(v) => st({ brightness: v })} testid="section-brightness" />
        <Color label="Цвят на слоя отгоре" value={s.overlay_color} onChange={(v) => st({ overlay_color: v })} />
        <Num label="Плътност на слоя" value={Math.round((s.overlay_opacity || 0) * 100)} max={95} unit="%" onChange={(v) => st({ overlay_opacity: v / 100 })} testid="section-overlay" />
      </Group>
    </div>
  );
}

export function HeaderPanel({ header, lang, set }) {
  const nav = header.nav || [];
  const setNav = (n) => set({ nav: n });
  return (
    <div className="space-y-3" data-testid="header-panel">
      <div className="font-semibold text-forest-deep">Горно меню и лого</div>
      <ImageField label="Лого" value={header.logo_url} onChange={(v) => set({ logo_url: v })} testid="header-logo" />
      <Num label="Височина на логото" value={header.logo_height} min={20} max={140} unit="px" onChange={(v) => set({ logo_height: v })} testid="header-logo-height" />
      <div className="grid grid-cols-2 gap-2"><Num label="Лого X" value={header.logo_offset_x} min={-100} max={100} onChange={(v) => set({ logo_offset_x: v })} /><Num label="Лого Y" value={header.logo_offset_y} min={-40} max={40} onChange={(v) => set({ logo_offset_y: v })} /></div>
      <Num label="Височина на менюто" value={header.height} min={56} max={160} unit="px" onChange={(v) => set({ height: v })} />
      <div className="grid grid-cols-3 gap-2"><Color label="Фон" value={header.bg} onChange={(v) => set({ bg: v })} testid="header-bg" /><Color label="Текст" value={header.text} onChange={(v) => set({ text: v })} /><Color label="Активен" value={header.active} onChange={(v) => set({ active: v })} /></div>
      <Group title={`Връзки в менюто (${lang.toUpperCase()})`}>
        <SortableList ids={nav.map((n) => n.id)} onReorder={(ids) => setNav(ids.map((id) => nav.find((n) => n.id === id)))}>
          {(id, handle) => {
            const n = nav.find((x) => x.id === id);
            const upd = (p) => setNav(nav.map((x) => (x.id === id ? { ...x, ...p } : x)));
            return (
              <div className="border border-gold/15 rounded-lg p-1.5 space-y-1 bg-white">
                <div className="flex items-center gap-1">{handle}
                  <input value={n.label?.[lang] || ""} placeholder={n.label?.bg} onChange={(e) => upd({ label: { ...n.label, [lang]: e.target.value } })} className="flex-1 h-8 border rounded px-2 text-sm" data-testid={`nav-label-${id}`} />
                  <button onClick={() => upd({ hidden: !n.hidden })} className="p-1" data-testid={`nav-hide-${id}`}>{n.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  <button onClick={() => setNav(nav.filter((x) => x.id !== id))} className="p-1 text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                <input value={n.href} onChange={(e) => upd({ href: e.target.value })} className="w-full h-7 border rounded px-2 text-xs text-muted-foreground" />
              </div>
            );
          }}
        </SortableList>
        <Button size="sm" variant="outline" className="gap-1" onClick={() => setNav([...nav, { id: uid(), label: same("Нова връзка"), href: "/site", hidden: false }])} data-testid="nav-add"><Plus className="w-4 h-4" /> Връзка</Button>
      </Group>
    </div>
  );
}

export function ThemePanel({ theme, set }) {
  return (
    <div className="space-y-3" data-testid="theme-panel">
      <div className="font-semibold text-forest-deep">Цветове и шрифтове на сайта</div>
      <p className="text-xs text-muted-foreground">Кликнете върху елемент в страницата, за да го редактирате.</p>
      <div className="grid grid-cols-2 gap-2">
        <Color label="Основен" value={theme.primary} onChange={(v) => set({ primary: v })} testid="theme-primary" />
        <Color label="Акцент (злато)" value={theme.accent} onChange={(v) => set({ accent: v })} testid="theme-accent" />
        <Color label="Фон на страниците" value={theme.bg} onChange={(v) => set({ bg: v })} testid="theme-bg" />
        <Color label="Текст" value={theme.text} onChange={(v) => set({ text: v })} />
      </div>
      <Pick label="Шрифт на заглавията" value={theme.heading_font} onChange={(v) => set({ heading_font: v })} options={FONTS.map((f) => [f, f])} testid="theme-heading-font" />
      <Pick label="Шрифт на текста" value={theme.body_font} onChange={(v) => set({ body_font: v })} options={FONTS.map((f) => [f, f])} testid="theme-body-font" />
    </div>
  );
}

export const newBlock = (type) => block(type);
